import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import useDocumentTitle from '../../hooks/useDocumentTitle';

interface Post {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content_html: string;
  cover_image_url: string;
  cover_image_alt: string;
  meta_title: string;
  meta_description: string;
  tags: string;
  author_name: string;
  published_at: string;
}

const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<Post | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useDocumentTitle(post ? `${post.meta_title || post.title} | CloudAyn Blog` : 'Loading... | CloudAyn Blog', false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
        const res = await fetch(`${apiUrl}/v1/public/blog/${slug}`);
        if (res.status === 404) { setNotFound(true); setLoading(false); return; }
        const data = await res.json();
        if (data.status === 'success') {
          setPost(data.data.post);
          // Update meta description dynamically
          const metaDesc = document.querySelector('meta[name="description"]');
          if (metaDesc && data.data.post.meta_description) {
            metaDesc.setAttribute('content', data.data.post.meta_description);
          }
        } else {
          setNotFound(true);
        }
      } catch { setNotFound(true); }
      setLoading(false);
    };
    if (slug) fetchPost();
  }, [slug]);

  return (
    <div style={{ minHeight: '100vh', background: '#ffffff' }}>
      {/* Nav */}
      <nav className="navbar navbar-light bg-white border-bottom px-3 py-3 sticky-top">
        <div className="container d-flex justify-content-between align-items-center">
          <Link to="/" className="navbar-brand d-flex align-items-center text-decoration-none">
            <div style={{ width: 36, height: 36, background: 'linear-gradient(135deg,#06b6d4,#3b82f6)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 800 }}>C</div>
            <span style={{ fontWeight: 800, fontSize: '1.15rem', marginLeft: 8, background: 'linear-gradient(135deg,#06b6d4,#3b82f6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CloudAyn</span>
          </Link>
          <div className="d-flex align-items-center gap-2">
            <Link to="/blog" className="text-muted text-decoration-none small me-2">← All Posts</Link>
            <Link to="/signup" className="btn btn-primary btn-sm">Get Started Free</Link>
          </div>
        </div>
      </nav>

      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="spinner-border text-primary"></div>
        </div>
      ) : notFound ? (
        <div className="text-center py-5">
          <h2 className="fw-bold">Post not found</h2>
          <p className="text-muted">This post may have been removed or the URL is incorrect.</p>
          <Link to="/blog" className="btn btn-outline-primary mt-2">Back to Blog</Link>
        </div>
      ) : post && (
        <article>
          {post.cover_image_url && (
            <div style={{ width: '100%', maxHeight: 480, overflow: 'hidden' }}>
              <img
                src={post.cover_image_url}
                alt={post.cover_image_alt || post.title}
                style={{ width: '100%', maxHeight: 480, objectFit: 'cover' }}
              />
            </div>
          )}

          <div className="container py-5">
            <div className="row justify-content-center">
              <div className="col-lg-8">
                {/* Tags */}
                {post.tags && (
                  <div className="d-flex gap-2 flex-wrap mb-3">
                    {post.tags.split(',').map(t => (
                      <span key={t} className="badge" style={{ background: '#eff6ff', color: '#3b82f6', fontSize: '0.7rem', fontWeight: 700 }}>{t.trim()}</span>
                    ))}
                  </div>
                )}

                {/* Title */}
                <h1 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.2rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.3, marginBottom: '1rem' }}>
                  {post.title}
                </h1>

                {/* Meta */}
                <div className="d-flex align-items-center gap-3 mb-4 pb-4 border-bottom" style={{ fontSize: '0.85rem', color: '#64748b' }}>
                  <span><i className="bi bi-person me-1"></i>{post.author_name}</span>
                  {post.published_at && (
                    <span><i className="bi bi-calendar3 me-1"></i>{new Date(post.published_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
                  )}
                </div>

                {/* Excerpt */}
                {post.excerpt && (
                  <p style={{ fontSize: '1.1rem', color: '#475569', lineHeight: 1.7, marginBottom: '2rem', fontStyle: 'italic', borderLeft: '3px solid #3b82f6', paddingLeft: '1rem' }}>
                    {post.excerpt}
                  </p>
                )}

                {/* Content */}
                <div
                  className="blog-post-content"
                  style={{ lineHeight: 1.85, color: '#374151', fontSize: '1rem' }}
                  dangerouslySetInnerHTML={{ __html: post.content_html || '' }}
                />

                {/* CTA */}
                <div className="mt-5 p-4 rounded-3 text-center" style={{ background: 'linear-gradient(135deg,#f0f9ff,#e0f2fe)', border: '1px solid #bae6fd' }}>
                  <h4 className="fw-bold mb-2" style={{ color: '#0369a1' }}>Ready to try CloudAyn?</h4>
                  <p className="text-muted mb-3 small">The best analytics, uptime monitoring, and AI SEO tool — all in one platform.</p>
                  <Link to="/signup" className="btn btn-primary">Get Started Free — No Card Required</Link>
                </div>
              </div>
            </div>
          </div>
        </article>
      )}

      {/* Inline styles for blog content */}
      <style>{`
        .blog-post-content h2 { font-size: 1.4rem; font-weight: 700; color: #0f172a; margin-top: 2rem; margin-bottom: 0.75rem; }
        .blog-post-content h3 { font-size: 1.15rem; font-weight: 700; color: #1e293b; margin-top: 1.5rem; margin-bottom: 0.5rem; }
        .blog-post-content p { margin-bottom: 1.25rem; }
        .blog-post-content ul, .blog-post-content ol { margin-bottom: 1.25rem; padding-left: 1.5rem; }
        .blog-post-content li { margin-bottom: 0.4rem; }
        .blog-post-content blockquote { border-left: 4px solid #3b82f6; padding-left: 1rem; color: #64748b; font-style: italic; margin: 1.5rem 0; }
        .blog-post-content img { max-width: 100%; border-radius: 8px; margin: 1rem 0; }
        .blog-post-content a { color: #3b82f6; }
        .blog-post-content code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 0.88em; }
        .blog-post-content pre { background: #1e293b; color: #e2e8f0; padding: 1.25rem; border-radius: 8px; overflow-x: auto; margin-bottom: 1.25rem; }
      `}</style>

      <footer className="border-top py-4 bg-light text-center mt-3">
        <p className="text-muted small mb-1">&copy; 2026 CloudAyn by INFIYO TECHNOLOGIES WLL</p>
        <div className="d-flex justify-content-center gap-3">
          <Link to="/" className="text-muted small text-decoration-none">Home</Link>
          <Link to="/blog" className="text-muted small text-decoration-none">Blog</Link>
          <Link to="/privacy" className="text-muted small text-decoration-none">Privacy</Link>
        </div>
      </footer>
    </div>
  );
};

export default BlogPostPage;
